import { type AutomationHealthData } from '@/hooks/admin/useAdminAutomationHealth';
import { AdminTabShell } from './AdminTabShell';

interface AutomationHealthTabProps {
  data: AutomationHealthData | null;
  isLoading: boolean;
  error: Error | null;
}

function formatTriggerType(t: string): string {
  return t.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function AutomationHealthTab({ data, isLoading, error }: AutomationHealthTabProps) {
  return (
    <AdminTabShell isLoading={isLoading} error={error}>
      {data && (
        <div className="space-y-4">
          {/* Summary cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-wt-bg-secondary border border-wt-border rounded-lg p-3">
              <div className="text-xs text-wt-text-muted uppercase">Active Rules</div>
              <div className="text-2xl font-mono text-wt-text-primary mt-1">{data.totalRules}</div>
            </div>
            <div className="bg-wt-bg-secondary border border-wt-border rounded-lg p-3">
              <div className="text-xs text-wt-text-muted uppercase">Triggers (24h)</div>
              <div className="text-2xl font-mono text-wt-text-primary mt-1">{data.recentTriggers24h}</div>
            </div>
          </div>

          {/* Two-column layout: rules by type + triggers by type */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Rules by type */}
            <div className="border border-wt-border rounded-lg overflow-hidden">
              <div className="bg-wt-bg-tertiary px-3 py-2 text-xs text-wt-text-muted uppercase tracking-wider">
                Rules by Trigger Type
              </div>
              <div className="divide-y divide-wt-border">
                {data.rulesByType.length === 0 ? (
                  <div className="px-3 py-4 text-sm text-wt-text-muted text-center">No active rules</div>
                ) : (
                  data.rulesByType.map((r) => (
                    <div key={r.trigger_type} className="flex items-center justify-between px-3 py-2 text-sm">
                      <span className="text-wt-text-secondary">{formatTriggerType(r.trigger_type)}</span>
                      <span className="font-mono text-wt-text-primary">{r.count}</span>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Triggers by event kind (24h) */}
            <div className="border border-wt-border rounded-lg overflow-hidden">
              <div className="bg-wt-bg-tertiary px-3 py-2 text-xs text-wt-text-muted uppercase tracking-wider">
                Notifications by Event Kind (24h)
              </div>
              <div className="divide-y divide-wt-border">
                {data.triggersByType.length === 0 ? (
                  <div className="px-3 py-4 text-sm text-wt-text-muted text-center">No notifications sent</div>
                ) : (
                  data.triggersByType.map((t) => (
                    <div key={t.event_kind} className="flex items-center justify-between px-3 py-2 text-sm">
                      <span className="text-wt-text-secondary">{formatTriggerType(t.event_kind)}</span>
                      <span className="font-mono text-wt-text-primary">{t.count}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminTabShell>
  );
}
